import React, { FC, useState } from "react";
import { Box } from "@mui/material";
import classNames from "classnames";
import toast from "react-hot-toast";
import axiosInstant from "../../../../api";
import { productPdpUrlVideo } from "../../../../api/endpoints";
import { ConfirmationDialog } from "../../../../components/ConfirmationDialog/ConfirmationDialog";
import {
    PdpScoreStatus,
    PdpUrlAttempt,
    PdpUrlScore
} from "../../../../components/PdpScoreStatus/PdpScoreStatus";
import { useUser } from "../../../../hooks/useUser";
import { isInternal } from "../../../../utils";
import { IconGoToTab, IconLink } from "../../../../utils/icons";
import { formatVideoName } from "../hooks/useFetchVideos";
import { PdpUrlVideoLink } from "../../../../components/PdpPages/PdpJsonLdOutput";
import { PageAssignment, pdpVideoHref } from "./pdpAssignment";

/**
 * Putting a video on a product page — the pieces the Product Pages tab of
 * the video settings modal (PdpUrlsManager) renders around its existing form.
 *
 * A product's pages exist before its videos do: they are added from the
 * product's own settings, with no video on them. When a video arrives,
 * someone picks the page it sits on. That creates this video's link on the
 * page (`desires_pdp_url_videos`, TDC-1644) — and a page carries any number
 * of our videos, so a page other videos are on is joined, not taken. The
 * page's score then shows on this video's details page too.
 *
 * Kept out of PdpUrlsManager so that file changes in a handful of small
 * hunks. Named AssignPage rather than PdpAssignment: the helper module is
 * pdpAssignment.ts, and on a case-insensitive filesystem the two names
 * would collide.
 */

export interface AssignablePage {
    id: number;
    pdp_url: string;
    /** Our videos on the page, from the list endpoint — for "Also on: <names>". */
    videos?: PdpUrlVideoLink[] | null;
    latest_score?: PdpUrlScore | null;
    latest_attempt?: PdpUrlAttempt | null;
}

const CHIP: Record<PageAssignment, { label: string; className: string }> = {
    mine: { label: "Yours", className: "bg-tint text-accent-text border-transparent" },
    unassigned: { label: "No video", className: "bg-transparent text-muted border-border-token" },
    other: { label: "Other video", className: "bg-surface-2 text-muted border-transparent" }
};

/** The status word on a page's tab. */
export const AssignmentChip: FC<{ status: PageAssignment }> = ({ status }) => (
    <span
        className={classNames(
            "inline-flex items-center rounded-full border px-[7px] text-[10px] font-semibold uppercase tracking-[0.4px] leading-[16px]",
            CHIP[status].className
        )}
    >
        {CHIP[status].label}
    </span>
);

interface AssignArgs {
    desireid: number;
    page: AssignablePage;
    videoId?: number;
    /** After the write lands: the caller refetches, lands on this row, and starts the JSON-LD watch. */
    onAssigned: (pageId: number) => Promise<void> | void;
}

/**
 * The one write: `PUT .../videos/:fileId` with an empty body — the link is
 * created with nothing typed on it, so it never routes through Save's
 * validation. The next PDP analysis (or the file's known placement) fills
 * in the file URL, and the server then generates this video's JSON-LD for
 * the page when the video has a transcript.
 */
const useAssignPage = ({ desireid, page, videoId, onAssigned }: AssignArgs) => {
    const [busy, setBusy] = useState(false);
    const assign = async () => {
        if (!videoId || busy) return;
        setBusy(true);
        try {
            await axiosInstant.put(productPdpUrlVideo(desireid, page.id, videoId), {});
            toast.success("Video added to this page");
            await onAssigned(page.id);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Couldn't add this video to the page. Please try again.");
        } finally {
            setBusy(false);
        }
    };
    return { assign, busy };
};

// Both conditions are real: maybeGenerateJsonLd returns early without a video
// file URL, and again when the video has no transcript. Stating it
// unconditionally promised a rewrite that frequently does not happen.
const CONSEQUENCE =
    "Its JSON-LD for this video is generated from the transcript once the page's video file URL for it is known (the next PDP analysis finds it) and the video has a transcript.";

// The tab's accent action, in the same treatment as the Save button below it
// (not the same metrics — Save is px-[22px] py-[10px] text-[13.5px]).
const assignBtnCls =
    "self-start px-[18px] py-[9px] rounded-[9px] text-[13px] font-semibold text-white bg-accent hover:bg-[#967435] transition-colors disabled:opacity-60";

const labelCls = "block text-[12px] font-semibold text-text-2 uppercase tracking-[0.3px] mb-[7px]";

/** A page with no video on it: sits above the form, where the old "saving here links it" strip was. */
export const AssignPageBlock: FC<AssignArgs> = ({ desireid, page, videoId, onAssigned }) => {
    const { assign, busy } = useAssignPage({ desireid, page, videoId, onAssigned });
    return (
        <Box className="flex flex-col gap-[6px] rounded-[10px] border border-[#EAD9B8] bg-tint px-4 py-3 mb-4">
            <span className="text-[13px] font-semibold text-text-1">No video on this page</span>
            <span className="text-[12.5px] text-text-2">
                This is one of the product&apos;s pages, but none of our videos is on it yet. Add this
                video and the page&apos;s score shows on the video&apos;s page.
            </span>
            <span className="text-[11.5px] text-muted">{CONSEQUENCE}</span>
            <button
                type="button"
                onClick={assign}
                disabled={busy || !videoId}
                className={classNames(assignBtnCls, "mt-[6px]")}
            >
                {busy ? "Adding…" : "Add this video to the page"}
            </button>
        </Box>
    );
};

/** One "name · View video" line per video on the page. */
const OtherVideoLinks: FC<{ links: PdpUrlVideoLink[] }> = ({ links }) => {
    const { role } = useUser();
    return (
        <Box className="flex flex-col gap-[4px]">
            {links.map((link) => {
                const name = formatVideoName(link.videoFile?.name || link.videoFile?.key) || `Video ${link.s3_file_id}`;
                const href = pdpVideoHref(isInternal(role), link.s3_file_id);
                return (
                    <Box key={link.id} className="flex items-center gap-3 flex-wrap text-[12.5px] text-text-2">
                        <span className="font-semibold text-text-1">{name}</span>
                        {href && (
                            <a
                                href={href}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-[5px] text-accent-text hover:underline"
                            >
                                <IconGoToTab width={13} height={13} stroke="currentColor" /> View video
                            </a>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

/**
 * A page other videos are on (and this one is not): what it is, which videos,
 * its score, and the same action behind a confirm — adding this video joins
 * the page; the others stay. No form — editing this video's details on the
 * page comes once it is on it, and the page itself belongs to the product's
 * own settings.
 */
export const OtherVideoSummary: FC<AssignArgs> = ({ desireid, page, videoId, onAssigned }) => {
    const { assign, busy } = useAssignPage({ desireid, page, videoId, onAssigned });
    const [confirmOpen, setConfirmOpen] = useState(false);

    // This renders only under assignment === "other", so there is at least one.
    const others = page.videos || [];
    const holderNames = others
        .map((l) => formatVideoName(l.videoFile?.name || l.videoFile?.key) || `Video ${l.s3_file_id}`)
        .join(", ");

    return (
        <Box className="flex flex-col gap-4">
            <Box className="flex flex-col gap-[6px] min-w-0">
                <a
                    href={page.pdp_url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-[6px] text-[13.5px] text-text-1 hover:text-accent-text min-w-0"
                >
                    {/* stroke is an SVG attribute and cannot read var(); inherit --muted via currentColor */}
                    <span className="text-muted shrink-0 flex">
                        <IconLink size={15} stroke="currentColor" />
                    </span>
                    <span className="truncate">{page.pdp_url}</span>
                </a>
                <span className="text-[12.5px] text-text-2">Already on this page:</span>
                <OtherVideoLinks links={others} />
            </Box>

            <Box className="pt-4 border-t border-border-token">
                <span className={labelCls}>Page score</span>
                <PdpScoreStatus score={page.latest_score} attempt={page.latest_attempt} />
            </Box>

            <Box className="flex flex-col gap-2 pt-4 border-t border-border-token">
                <span className="text-[12.5px] text-text-2">
                    Adding this video joins the page alongside {others.length === 1 ? "it" : "them"} — the
                    page&apos;s score shows on this video too.
                </span>
                <button
                    type="button"
                    onClick={() => setConfirmOpen(true)}
                    disabled={busy || !videoId}
                    className={assignBtnCls}
                >
                    {busy ? "Adding…" : "Add this video to the page"}
                </button>
            </Box>

            <ConfirmationDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={async () => {
                    setConfirmOpen(false);
                    await assign();
                }}
                title="Add this video to this page?"
                description={`${holderNames} ${others.length === 1 ? "is" : "are"} already on it and stay${others.length === 1 ? "s" : ""}. This video gets its own VideoObject for the page — generated from its transcript once the page's file URL for it is known — and the page's score shows on this video too.`}
                confirmLabel="Add video"
                cancelLabel="Cancel"
                disabled={busy}
            />
        </Box>
    );
};
